import React, { useState, useRef, useEffect } from 'react';
import { Typography, IconButton, TextField, Fade } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import styles from './ChatMessage.module.css';
import clsx from 'clsx';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store/store';
import { updateMessageAsync } from '../../store/projectSlice';
import { Rating as RatingType } from '../../types';

interface ChatMessageProps {
  message: string;
  sender: string;
  timestamp: string;
  isBot?: boolean;
  messageId?: number;
  projectId?: number;
  savedRating?: RatingType;
  savedComment?: string;
}

const ChatMessage: React.FC<ChatMessageProps> = ({ 
  message, 
  sender, 
  timestamp, 
  isBot = false, 
  messageId, 
  projectId,
  savedRating,
  savedComment: initialComment = ''
}) => {
  const dispatch = useDispatch<AppDispatch>();
  
  // Convert between UI rating state and API rating format
  const mapApiRatingToUi = (apiRating?: RatingType): 'up' | 'down' | null => {
    if (apiRating === 'thumbs_up') return 'up';
    if (apiRating === 'thumbs_down') return 'down';
    return null;
  };
  
  const mapUiRatingToApi = (uiRating: 'up' | 'down' | null): RatingType => {
    if (uiRating === 'up') return 'thumbs_up';
    if (uiRating === 'down') return 'thumbs_down';
    return 'none';
  };
  
  const [rating, setRating] = useState<'up' | 'down' | null>(mapApiRatingToUi(savedRating));
  const [showComment, setShowComment] = useState(false);
  const [comment, setComment] = useState(initialComment || '');
  const [savedComment, setSavedComment] = useState(initialComment || '');
  const [showSavedIndicator, setShowSavedIndicator] = useState(false);
  
  const handleCommentBlur = () => {
    if (comment !== savedComment && messageId && projectId) {
      // Save to local state immediately for UI feedback
      setSavedComment(comment);
      setShowSavedIndicator(true);
      
      // Persist to backend
      dispatch(updateMessageAsync({
        projectId,
        messageId,
        data: { comment: comment || undefined }
      }));
      
      setTimeout(() => setShowSavedIndicator(false), 1500);
    }
  };
  
  const handleDeleteComment = () => {
    setComment('');
    setSavedComment('');
    setShowComment(false);
    
    // Persist deletion to backend if we have message info
    if (messageId && projectId) {
      dispatch(updateMessageAsync({
        projectId,
        messageId,
        data: { comment: "" }
      }));
    }
  };
  
  // UI event handler when user clicks a rating button
  const handleRatingClick = (type: 'up' | 'down') => {
    // Toggle the rating if already selected
    const newRating = rating === type ? null : type;
    handleRatingChange(newRating);
  };

  // UI event handler for rating keyboard interaction
  const handleRatingKeyPress = (e: React.KeyboardEvent, type: 'up' | 'down') => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleRatingClick(type);
    }
  };
  
  const handleRatingChange = (newRating: 'up' | 'down' | null) => {
    setRating(newRating);
    
    // If we have the necessary IDs, persist the rating
    if (messageId && projectId) {
      dispatch(updateMessageAsync({ 
        projectId, 
        messageId, 
        data: { rating: mapUiRatingToApi(newRating) }
      }));
    }
  };
  
  // Compute if a rating is selected
  const isUpSelected = rating === 'up';
  const isDownSelected = rating === 'down';
  
  return (
    <div className={clsx(styles.outerContainer, isBot ? styles.botOuterContainer : styles.userOuterContainer)}>
      <div className={clsx(styles.messageContainer, isBot ? styles.botMessage : styles.userMessage)}>
        <div className={clsx(styles.messageBox, isBot ? styles.botMessageBox : styles.userMessageBox)}>
          <Typography
            variant="subtitle2"
            className={clsx(styles.sender, isBot ? styles.botSender : styles.userSender)}
          >
            {sender}
          </Typography>
          
          {isBot ? (
            <div className={styles.markdownContent}>
              <ReactMarkdown
                components={{
                  code: ({className, children, ...props}) => {
                    const match = /language-(\w+)/.exec(className || '');
                    return match ? (
                      <SyntaxHighlighter
                        style={vscDarkPlus}
                        language={match[1]}
                        PreTag="div"
                        className={styles.codeBlock}
                        {...props}
                      >
                        {String(children).replace(/\n$/, '')}
                      </SyntaxHighlighter>
                    ) : (
                      <code className={className} {...props}>
                        {children}
                      </code>
                    );
                  }
                }}
              >
                {message}
              </ReactMarkdown>
            </div>
          ) : (
            <Typography className={styles.userMessageContent}>
              {message}
            </Typography>
          )}
          
          <Typography
            variant="caption"
            className={clsx(styles.timestamp, !isBot && styles.userTimestamp)}
          >
            {timestamp}
          </Typography>
          
          {isBot && (
            <div className={styles.messageActionsContainer}>
              <div className={styles.ratingActions}>
                <IconButton 
                  className={clsx(styles.ratingButton, isUpSelected && styles.ratingButtonSelected)} 
                  aria-label="thumbs up"
                  onClick={() => handleRatingClick('up')}
                  onKeyDown={(e) => handleRatingKeyPress(e, 'up')}
                  size="small"
                >
                  {isUpSelected ? <ThumbUpIcon fontSize="small" className={styles.iconSmall} /> : <ThumbUpOutlinedIcon fontSize="small" className={styles.iconSmall} />}
                </IconButton>
                <IconButton 
                  className={clsx(styles.ratingButton, isDownSelected && styles.ratingButtonSelected)} 
                  aria-label="thumbs down"
                  onClick={() => handleRatingClick('down')}
                  onKeyDown={(e) => handleRatingKeyPress(e, 'down')}
                  size="small"
                >
                  {isDownSelected ? <ThumbDownIcon fontSize="small" className={styles.iconSmall} /> : <ThumbDownOutlinedIcon fontSize="small" className={styles.iconSmall} />}
                </IconButton>
                <IconButton 
                  size="small" 
                  onClick={() => setShowComment(!showComment)} 
                  className={styles.ratingButton}
                  aria-label="comment"
                >
                  <EditIcon fontSize="small" className={styles.iconSmall} />
                  {!showComment && savedComment && (
                    <span className={styles.commentIndicator}>*</span>
                  )}
                </IconButton>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {isBot && showComment && (
        <div className={styles.messageCommentRow}>
          <div className={styles.commentInputContainer}>
            <div className={styles.commentInputWrapper}>
              <TextField
                className={styles.commentInput}
                placeholder="Enter comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                onBlur={handleCommentBlur}
                variant="outlined"
                size="small"
                fullWidth
                multiline
                rows={2}
              />
              <IconButton 
                className={styles.commentDeleteButton}
                size="small"
                onClick={handleDeleteComment}
                aria-label="delete comment"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
              <Fade in={showSavedIndicator}>
                <Typography variant="caption" className={styles.savedIndicator}>
                  Saved
                </Typography>
              </Fade>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatMessage;
